import { useState, useEffect } from 'react';
import { seoAPI } from '../services/apiService';

// Custom hook để quản lý SEO cho các trang
export const useSEO = (pageKey, defaultSEO = {}) => {
  const [seoData, setSeoData] = useState({
    h1: defaultSEO.h1 || '',
    metaDescription: defaultSEO.metaDescription || '',
    title: defaultSEO.title || '',
    ...defaultSEO
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSeoSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`🔍 Loading SEO settings for page: ${pageKey}`);
        
        // Try to get SEO settings from API
        const settings = await seoAPI.getSettings();
        console.log('🔍 API returned settings:', settings);
        
        if (settings && settings[pageKey]) {
          const pageSEO = settings[pageKey];
          console.log(`✅ Found SEO settings for ${pageKey}:`, pageSEO);
          setSeoData(pageSEO);
          
          // Force update title immediately
          if (pageSEO.title) {
            console.log(`🚀 Force updating title to: ${pageSEO.title}`);
            document.title = pageSEO.title;
          }
          
        } else {
          console.log(`❌ No SEO settings found for ${pageKey}, using fallback`);
          // Fallback to localStorage if API fails
          const localSeoSettings = localStorage.getItem('seo_settings');
          if (localSeoSettings) {
            const localSettings = JSON.parse(localSeoSettings);
            if (localSettings[pageKey]) {
              const pageSEO = localSettings[pageKey];
              setSeoData(pageSEO);
              console.log(`📦 Using localStorage settings for ${pageKey}:`, pageSEO);
            }
          }
        }
      } catch (err) {
        console.warn(`Failed to load SEO settings for ${pageKey}:`, err);
        setError(err);
        
        // Fallback to localStorage
        try {
          const localSeoSettings = localStorage.getItem('seo_settings');
          if (localSeoSettings) {
            const localSettings = JSON.parse(localSeoSettings);
            if (localSettings[pageKey]) {
              setSeoData(localSettings[pageKey]);
              console.log(`📦 Using localStorage fallback for ${pageKey}`);
            }
          }
        } catch (localErr) {
          console.warn('Failed to load local SEO settings:', localErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSeoSettings();
  }, [pageKey]);

  // Update document title and meta tags when seoData changes
  useEffect(() => {
    console.log(`🔍 useSEO hook updating for page: ${pageKey}`, seoData);
    
    if (seoData.title) {
      console.log(`📝 Setting document title to: ${seoData.title}`);
      document.title = seoData.title;
    }
    
    if (seoData.metaDescription) {
      console.log(`📝 Setting meta description to: ${seoData.metaDescription}`);
      updateMetaTag('description', seoData.metaDescription);
    }
    
    if (seoData.keywords) {
      updateMetaTag('keywords', seoData.keywords);
    }
    
    // Update Open Graph tags
    if (seoData.ogTitle || seoData.title) {
      updateMetaProperty('og:title', seoData.ogTitle || seoData.title);
    }
    
    if (seoData.ogDescription || seoData.metaDescription) {
      updateMetaProperty('og:description', seoData.ogDescription || seoData.metaDescription);
    }
    
    if (seoData.ogImage) {
      updateMetaProperty('og:image', seoData.ogImage);
    }
  }, [seoData, pageKey]);

  // Helper function to update meta tags
  const updateMetaTag = (name, content) => {
    const existingMeta = document.querySelector(`meta[name="${name}"]`);
    if (existingMeta) {
      existingMeta.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  };

  // Helper function to update meta property tags (like Open Graph)
  const updateMetaProperty = (property, content) => {
    const existingMeta = document.querySelector(`meta[property="${property}"]`);
    if (existingMeta) {
      existingMeta.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.content = content;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  };

  return {
    seoData,
    loading,
    error,
    setSeoData // Allow manual updates if needed
  };
};

export default useSEO; 